/*
 * Purpose: Error debugger — errors/warnings from logStore
 * Last modified: 2026-06-28 | Agent
 * Completeness: 90
 */
import { useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertCircle, Bug, Copy, Download, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useLogStore } from '@/stores/logStore';
import { logsToDebugEntries, type DebugErrorEntry } from '@/lib/executionInsights';
import { Link } from 'react-router-dom';
import { APP_ROUTES } from '@/lib/appRoutes';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useExecutionTraceStore } from '@/stores/executionTraceStore';
import { getSmartBlockInputRequirements } from '@/services/blockAutoConfigure';

export function ErrorDebugger() {
  const logs = useLogStore((s) => s.logs);
  const errors = useMemo(() => logsToDebugEntries(logs), [logs]);
  const [selectedError, setSelectedError] = useState<DebugErrorEntry | null>(null);
  const [showStack, setShowStack] = useState(false);
  const currentWorkflow = useWorkflowStore((s) => s.currentWorkflow);
  const selectNode = useWorkflowStore((s) => s.selectNode);
  const trace = useExecutionTraceStore((s) =>
    currentWorkflow?.id ? s.traces[currentWorkflow.id] : undefined
  );

  const activeError = selectedError ?? errors.find((e) => e.severity === 'error') ?? null;

  const handleCopyError = () => {
    if (!activeError) return;
    const errorText = `Error: ${activeError.message}\nType: ${activeError.errorType}\nTime: ${activeError.timestamp.toLocaleString()}`;
    void navigator.clipboard.writeText(errorText).then(() => toast.success('Error copied to clipboard'));
  };

  const handleDownloadLog = () => {
    const logContent = errors
      .map(
        (err) =>
          `[${err.timestamp.toISOString()}] ${err.severity.toUpperCase()}: ${err.message}\nType: ${err.errorType}`
      )
      .join('\n---\n');
    const blob = new Blob([logContent || 'No errors logged'], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `error-log-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Error log downloaded');
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'error':
        return 'bg-red-500/10 text-red-700 border-red-500/20';
      case 'warning':
        return 'bg-amber-500/10 text-amber-700 border-amber-500/20';
      default:
        return 'bg-blue-500/10 text-blue-700 border-blue-500/20';
    }
  };

  if (errors.length === 0) {
    return (
      <Card className="p-6 text-center text-muted-foreground space-y-3">
        <p className="text-sm">No errors or warnings in execution logs yet.</p>
        <p className="text-xs">Run a flow from the Mission map — failures appear here automatically.</p>
        <Button asChild size="sm" variant="outline">
          <Link to={APP_ROUTES.missionMap}>Open Mission map</Link>
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Error Debugger</h2>
        <p className="text-muted-foreground text-sm">Live errors from flow execution logs</p>
      </div>

      <Tabs defaultValue="errors" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="errors">
            Errors ({errors.filter((e) => e.severity === 'error').length})
          </TabsTrigger>
          <TabsTrigger value="warnings">
            Warnings ({errors.filter((e) => e.severity === 'warning').length})
          </TabsTrigger>
          <TabsTrigger value="all">All ({errors.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="errors" className="space-y-4">
          {errors.filter((e) => e.severity === 'error').length === 0 ? (
            <Card className="p-6 text-center text-muted-foreground">
              <p className="text-sm">No errors detected.</p>
            </Card>
          ) : (
            <ErrorList
              items={errors.filter((e) => e.severity === 'error')}
              selected={activeError}
              onSelect={setSelectedError}
              getSeverityColor={getSeverityColor}
              showStack={showStack}
              setShowStack={setShowStack}
              onCopy={handleCopyError}
              onDownload={handleDownloadLog}
            />
          )}
        </TabsContent>

        <TabsContent value="warnings">
          <SimpleList items={errors.filter((e) => e.severity === 'warning')} getSeverityColor={getSeverityColor} />
        </TabsContent>

        <TabsContent value="all">
          <SimpleList items={errors} getSeverityColor={getSeverityColor} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SimpleList({
  items,
  getSeverityColor,
}: {
  items: DebugErrorEntry[];
  getSeverityColor: (s: string) => string;
}) {
  if (items.length === 0) {
    return (
      <Card className="p-6 text-center text-muted-foreground">
        <p className="text-sm">Nothing here.</p>
      </Card>
    );
  }
  return (
    <div className="grid gap-4">
      {items.map((error) => (
        <Card key={error.id} className={`p-4 ${getSeverityColor(error.severity)}`}>
          <p className="font-medium">{error.message}</p>
          <p className="text-xs mt-1 opacity-75">{new Date(error.timestamp).toLocaleString()}</p>
        </Card>
      ))}
    </div>
  );
}

function ErrorList({
  items,
  selected,
  onSelect,
  getSeverityColor,
  showStack,
  setShowStack,
  onCopy,
  onDownload,
}: {
  items: DebugErrorEntry[];
  selected: DebugErrorEntry | null;
  onSelect: (e: DebugErrorEntry) => void;
  getSeverityColor: (s: string) => string;
  showStack: boolean;
  setShowStack: (v: boolean) => void;
  onCopy: () => void;
  onDownload: () => void;
}) {
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <Card className="lg:col-span-1 overflow-hidden flex flex-col">
        <div className="p-4 border-b border-border">
          <h3 className="font-semibold text-foreground text-sm">Error Log</h3>
        </div>
        <div className="overflow-y-auto flex-1 max-h-96">
          {items.map((error) => (
            <button
              key={error.id}
              type="button"
              onClick={() => onSelect(error)}
              className={`w-full text-left p-3 border-b border-border hover:bg-muted/50 ${
                selected?.id === error.id ? 'bg-muted' : ''
              }`}
            >
              <p className="text-sm font-medium truncate">{error.message}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {new Date(error.timestamp).toLocaleTimeString()}
              </p>
            </button>
          ))}
        </div>
      </Card>

      {selected && (
        <Card className="lg:col-span-2 p-4 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-foreground">{selected.message}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {new Date(selected.timestamp).toLocaleString()}
              </p>
            </div>
            <Badge className={getSeverityColor(selected.severity)}>{selected.severity}</Badge>
          </div>
          <div className="p-3 rounded border border-border bg-muted/50">
            <p className="text-xs text-muted-foreground mb-1">Error Type</p>
            <p className="font-mono text-sm">{selected.errorType}</p>
          </div>
          <button
            type="button"
            onClick={() => setShowStack(!showStack)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
          >
            {showStack ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            Details
          </button>
          {showStack && (
            <div className="p-3 rounded border bg-muted/50 font-mono text-xs">{selected.message}</div>
          )}
          <div className="flex gap-2 pt-4 border-t">
            <Button variant="outline" size="sm" onClick={onCopy} className="flex-1">
              <Copy className="w-4 h-4 mr-2" />
              Copy
            </Button>
            <Button variant="outline" size="sm" onClick={onDownload} className="flex-1">
              <Download className="w-4 h-4 mr-2" />
              Download
            </Button>
          </div>
          {currentWorkflow && trace?.failedNodeId && (
            <Card className="p-3 bg-amber-500/5 border-amber-500/30">
              <p className="text-sm font-medium">Root-cause bundle</p>
              {(() => {
                const failed = currentWorkflow.nodes.find((n) => n.id === trace.failedNodeId);
                const upstream = currentWorkflow.edges
                  .filter((e) => e.to === trace.failedNodeId)
                  .map((e) => currentWorkflow.nodes.find((n) => n.id === e.from))
                  .filter(Boolean);
                const req = failed ? getSmartBlockInputRequirements(failed) : [];
                const tier = req.length > 0 ? 'human-required' : 'review-first';
                return (
                  <div className="mt-2 space-y-2 text-xs text-muted-foreground">
                    <p>Failed block: {failed?.subtype ?? trace.failedNodeId}</p>
                    <p>Upstream: {upstream.map((u: any) => u.subtype).join(', ') || 'none'}</p>
                    <p>Recommended tier: {tier}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-6 px-2 text-[11px]"
                      onClick={() => selectNode(trace.failedNodeId!)}
                    >
                      Open failed block
                    </Button>
                  </div>
                );
              })()}
            </Card>
          )}
        </Card>
      )}
    </div>
  );
}
